// The community surface's rooms — one place so the hub, the guide reader and
// any future community page wear the same worlds. Each space is built the
// house way (lib/company/theme.ts): ONE hue makes the creature, and the ground
// sits the fixed interval below it, pale, so the ink ladder keeps its contrast
// whichever room you are in and the three read as a family rather than three
// unrelated pages. The backdrop publishes its hue, so the whole ink ladder and
// the innernet mark in the crumb follow the room, and the creature EASES from
// one colour to the next as the ticker moves.
//
//   skills     the pale sage everyone has seen — unchanged — under a teal netti
//   guides     a warm sand under a leaf-green netti
//   resources  a cool sky under a violet netti

import { groundFor, organismFor, type CoTheme } from "../../lib/company/theme";
import type { CommunitySpace } from "../../lib/community/types";

/** The community's rooms. Guides, resources and about stand on PALE grounds —
 *  the ink ladder is the organism's hue at 19% lightness, so the contrast under
 *  it is unchanged — each a room you can feel you are standing in. */
const room = (hue: number, sat: number, ground = groundFor(hue, 52, 86)): CoTheme => ({
  ground,
  color: organismFor(hue, sat),
  age: 0.9,
});

/** SKILLS IS A NIGHT ROOM (2026-09-19). A deep blue-green, so netti stands in
 *  it as lit GLASS (the engine reads the body colour: this is well under the
 *  0.22 luma where it turns fully glass), and the shelf's pale cards float on
 *  it with real contrast. The solid below is what body/html are painted (the
 *  organism samples it); the gradient on top is the room's own wash
 *  (NIGHT_WASH), swapped in for the visitor's while the room is open. The ink
 *  ladder is restated locally by `.cm-night` (community.css). */
export const SKILLS_NIGHT: CoTheme = {
  ground: "hsl(193 58% 10%)",
  color: organismFor(172, 0.8, { light: 0.56, contrast: 0.5 }),
  age: 0.92,
};

/** A skill's own page: the same night, a step lighter than the shelf it came
 *  from, so arriving on one reads as walking further in. (A skill somebody
 *  SHARED keeps the ground its sharer chose — ShareView never reads this.) */
export const SKILL_PAGE_NIGHT: CoTheme = {
  ground: "hsl(188 44% 15%)",
  color: organismFor(170, 0.78, { light: 0.56, contrast: 0.5 }),
  age: 0.92,
};

export const CM_THEMES: Record<CommunitySpace, CoTheme> = {
  skills: SKILLS_NIGHT,
  // 2026-09-29: the two browsing rooms move their ground in beside their
  // creature instead of a fixed 59° below it. At −59° the guides sand and the
  // resources sky sat so far from netti that the room read as a beige page with
  // a green thing on it, and the tiles (a lift of the same beige) vanished into
  // it. Now each ground is a pale wash of netti's own family, a step warmer
  // (guides: a lime-cream under the leaf) or cooler (resources: a periwinkle
  // under the violet), so the creature, the ground and the ink read as one.
  guides: room(96, 0.66, "hsl(74 50% 87%)"),
  resources: room(281, 0.66, "hsl(252 52% 91%)"),
  // a warm sand-cream, quieter than guides: the room you read, not browse
  about: room(74, 0.58),
};

/** which rooms are night rooms (dark ground, light ink, glass netti) */
export const NIGHT_SPACES: ReadonlySet<CommunitySpace> = new Set(["skills"]);

/** the wash a night room lays over the page: the four stops the house
 *  gradient reads (--g-a · --g-b · --g-c · --g-top, globals.css --ground-grad). */
export interface Wash { a: string; b: string; c: string; top: string }
export const SKILLS_WASH: Wash = {
  a: "hsl(204 72% 24% / 0.62)",
  b: "hsl(168 64% 22% / 0.5)",
  c: "hsl(186 74% 20% / 0.55)",
  top: "hsl(208 60% 16% / 0.55)",
};
export const SKILL_PAGE_WASH: Wash = {
  a: "hsl(196 60% 26% / 0.5)",
  b: "hsl(164 56% 24% / 0.42)",
  c: "hsl(182 62% 24% / 0.45)",
  top: "hsl(200 50% 20% / 0.45)",
};

/** the page ground of the community's front room (skills). */
export const CM_GROUND = CM_THEMES.skills.ground;
